
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/useAuthSession';
import { toast } from 'sonner';
import { StudyNote, AdaptiveNoteOptions, CurrentAffairsAdaptiveOptions } from '@/types/notes';
import { useQuizAnalysis } from '@/hooks/useQuizAnalysis';

export function useAdaptiveNotes() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [swotAnalysis, setSwotAnalysis] = useState<any>(null);
  const { session } = useAuthSession();
  const { analyzeQuizHistory } = useQuizAnalysis();
  const userId = session?.user?.id;

  const generateAdaptiveNotes = async (
    subject: string,
    category: string,
    options?: AdaptiveNoteOptions
  ): Promise<StudyNote | null> => {
    if (!userId) {
      toast.error('You must be logged in to generate adaptive notes');
      return null;
    }

    setIsGenerating(true);
    try {
      // Get quiz performance analysis
      const quizAnalysis = await analyzeQuizHistory(userId);

      // Get error patterns
      const { data: errorPatterns, error: errorError } = await supabase
        .from('error_pattern_analysis')
        .select('*')
        .eq('user_id', userId)
        .eq('subject', subject)
        .eq('resolved', false);

      // Get learning profile
      const { data: learningProfile, error: profileError } = await supabase
        .from('learning_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      // Get cognitive profile
      const { data: cognitiveProfile, error: cogError } = await supabase
        .from('cognitive_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      console.log('Collected user data for adaptive generation:', {
        quizAnalysis,
        errorPatterns: errorPatterns?.length || 0,
        hasLearningProfile: !!learningProfile,
        hasCognitiveProfile: !!cognitiveProfile,
        language: options?.language || 'en'
      });

      // Call the edge function with enriched context
      const { data, error } = await supabase.functions.invoke('adaptive-learning', {
        body: {
          action: 'generate_adaptive_notes',
          userId,
          subject,
          category,
          language: options?.language || 'en', // Pass the language, default to English
          performanceData: {
            quizAnalysis,
            errorPatterns: errorPatterns || [],
            learningProfile: learningProfile || null,
            cognitiveProfile: cognitiveProfile || null
          },
          options: {
            ...options,
            generateVisualContent: true,
            formatForMarkdown: true,
            includeSpecialElements: true,
            adaptToPerformance: true
          }
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      // Create a new study note with the adaptive content
      const { data: noteData, error: noteError } = await supabase
        .from('study_notes')
        .insert({
          title: `Adaptive Notes: ${subject}`,
          content: data.content,
          subject,
          category,
          content_type: 'html',
          language: options?.language || 'en', // Store the language in the database
          created_by: userId,
          ai_generated: true,
          adaptive_analysis: {
            generatedFromPerformance: true,
            adaptationLevel: options?.difficulty || 'standard',
            quizAnalysis,
            errorPatterns: errorPatterns?.map(ep => ep.pattern_type) || [],
            learningProfile: learningProfile?.learning_style,
            cognitiveProfile: cognitiveProfile?.learning_style,
            focusAreas: data.focusAreas || [],
            performanceByTopic: quizAnalysis?.performanceByTopic || {}
          }
        })
        .select();

      if (noteError) {
        throw noteError;
      }

      // Generate SWOT analysis based on quiz performance
      generateSWOTAnalysis(subject, category);

      toast.success('Adaptive notes generated successfully!');
      
      return {
        id: noteData[0].id,
        title: noteData[0].title,
        content: noteData[0].content,
        subject: noteData[0].subject,
        category: noteData[0].category,
        fileUrl: noteData[0].file_url,
        contentType: noteData[0].content_type as "text" | "html",
        language: noteData[0].language,
        createdAt: noteData[0].created_at,
        updatedAt: noteData[0].updated_at,
        adaptiveAnalysis: noteData[0].adaptive_analysis
      };
    } catch (error) {
      console.error('Error generating adaptive notes:', error);
      toast.error('Failed to generate adaptive notes');
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const generateSWOTAnalysis = async (subject: string, category: string) => {
    if (!userId) return;

    try {
      console.log(`Generating SWOT analysis for ${subject}`);
      const { data, error } = await supabase.functions.invoke('adaptive-learning', {
        body: {
          action: 'analyze_strengths_weaknesses',
          userId,
          subject,
          category
        }
      });

      if (error) {
        console.error('SWOT analysis error:', error);
        throw error;
      }

      if (data?.analysis) {
        setSwotAnalysis(data.analysis);
        console.log('SWOT analysis completed');
      }
    } catch (error) {
      console.error('Error generating SWOT analysis:', error);
      // Don't throw or show toast here as this is a supplementary feature
    }
  };

  const adaptCurrentAffairsContent = async (
    content: string, 
    options?: CurrentAffairsAdaptiveOptions
  ): Promise<string | null> => {
    if (!userId) {
      toast.error('You must be logged in to use adaptive features');
      return null;
    }

    try {
      console.log('Adapting current affairs content');
      const { data, error } = await supabase.functions.invoke('adaptive-learning', {
        body: {
          action: 'adapt_current_affairs',
          userId,
          content,
          options
        }
      });

      if (error) {
        console.error('Error from edge function:', error);
        throw error;
      }

      console.log('Content adapted successfully');
      return data?.content || null;
    } catch (error) {
      console.error('Error adapting current affairs content:', error);
      toast.error('Failed to adapt content');
      return null;
    }
  };

  return {
    isGenerating,
    generateAdaptiveNotes,
    swotAnalysis,
    adaptCurrentAffairsContent
  };
}
